import React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import { Column } from 'react-table';

import { IconButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container/Container';
import { Divider } from '~shared/components/Divider';
import { IconChevron } from '~shared/components/icons/standard';
import { Spinner } from '~shared/components/Spinner/Spinner';
import { sortByProperty } from '~shared/components/Table/sortingFns/sortByProperty/sortByProperty';
import { Sort } from '~shared/components/Table/update/components/Sort';
import { useSocTable } from '~shared/components/Table/update/useSocTable';

import { QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';
import { WorkspaceFolder } from '../../../../api/workspaces/folders/types';

export const commonDateSorter = (a, b) =>
  sortByProperty(null, (val) => new Date(val).getTime())(
    'publishedDate' in a.original
      ? a.original.publishedDate
      : a.original.createdDate,
    'publishedDate' in b.original
      ? b.original.publishedDate
      : b.original.createdDate
  );

interface SortingRule<D> {
  id: keyof D & string;
  desc: boolean;
}
interface DialogQuizItemListProps<T extends object> {
  data: T[];
  workspaceName: string;
  onSelection?: (data) => void;
  onBack: () => void;
  isLoading?: boolean;
  searchQuery?: { id: string; value: string };
  columns?: ReadonlyArray<Column<T>>;
  filters?: ((data: T[], search?: string) => T[])[];
  parentId: string | null | number;
  disableRow?: (row) => boolean;
  rootIsFinal?: boolean;
  children?: React.ReactNode;
}

/**
 * Interactive and complete quiz list for use in modals
 * - Launch modal selectQuiz, copy to, and move for now
 *
 * The list handles all its own internalized states and only interacts
 * with the actual dialog when an item has been selected
 */
export const DialogQuizItemList = <T extends object>({
  onSelection,
  onBack,
  workspaceName,
  data,
  isLoading,
  searchQuery,
  columns,
  filters,
  parentId,
  disableRow,
  rootIsFinal,
  children,
}: DialogQuizItemListProps<T>) => {
  const staticSort = (a, b) => sortByProperty('type')(a.original, b.original);
  // action column aka chevron
  const actionColumn = React.useMemo(
    () => ({
      header: () => <></>,
      cell: ({ row }) =>
        row.original.type === 'folder' ? (
          <IconChevron direction={'right'} />
        ) : (
          <></>
        ),
    }),
    []
  );
  // hook ini
  const [sort, setSort] = React.useState<SortingRule<T>>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    id: 'modified',
    desc: true,
  });
  const {
    resolvedData,
    prepareRow,
    state: { currentSort },
    props: { headerGroups },
  } = useSocTable({
    filters,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore <- this is to make the chevrons show, ik i'll make a better solution later
    actions: true,
    actionColumn,
    data,
    initialSort: sort,
    columns,
    enableBulkActions: true,
    increment: Infinity,
    searchQuery,
    sortable: true,
    staticSort,
  });

  React.useEffect(() => {
    setSort(currentSort[0] as SortingRule<T>);
  }, [currentSort]);

  // wrapper for the table
  const TableWrapper = ({
    children,
    ...rest
  }: {
    children: React.ReactNode;
  }) => {
    return (
      <div className={'w-full relative max-h-full overflow-auto'} {...rest}>
        {children}
      </div>
    );
  };

  return (
    <Container className={'h-full'} width={1}>
      <Container
        row
        verticalCenter
        style={{ height: '60px', overflow: 'hidden' }}
      >
        <Container margin={'10px 0'}>
          <Divider />
        </Container>
        <Container row verticalCenter className={'py-5 px-2 max-w-full'}>
          {rootIsFinal ? (
            parentId !== null && (
              <IconButton onClick={onBack}>
                <IconChevron direction={'left'} />
              </IconButton>
            )
          ) : (
            <IconButton onClick={onBack}>
              <IconChevron direction={'left'} />
            </IconButton>
          )}
          <span
            className={
              'ml-3 font-display text-display leading-8 text-left truncate font-700 text-blue-900 hover:no-underline'
            }
          >
            {(data as (QuizOrFolder | WorkspaceFolder)[]).find(
              (item) => item.id === parentId
            )?.name || workspaceName}
          </span>
        </Container>
      </Container>
      <Container width={1}>
        <div className={'h-full w-full overflow-auto'}>
          <TableWrapper>
            {/* header row */}
            {headerGroups.map((headerGroup) => {
              return (
                <TableRow key={headerGroup.getHeaderGroupProps().key} header>
                  {headerGroup.headers.map((column) => {
                    return (
                      <TableCell
                        key={column.getHeaderProps().key}
                        header
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        column={column}
                      >
                        {column.id !== 'actions' && column.render('Header')}
                        <Sort
                          enabled={
                            column.canSort &&
                            column.isSorted &&
                            !column.doNotShowSort
                          }
                          descending={column.isSortedDesc}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {/* body rows */}
            {resolvedData.map((row) => {
              prepareRow(row);
              const isDisabled = disableRow ? disableRow(row.original) : false;
              return (
                <TableRow
                  key={row.getRowProps().key}
                  style={{
                    opacity: isDisabled ? 0.7 : 1,
                    cursor: isDisabled ? 'normal' : 'pointer',
                    borderBottom: '1px solid rgb(232, 237, 240)',
                  }}
                >
                  {row.cells.map((cell) => {
                    if (cell.column.id === 'selection') {
                      return (
                        <TableCell
                          key={cell.getCellProps().key}
                          column={cell.column}
                        >
                          {cell.render('Cell')}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={cell.getCellProps().key}
                        column={cell.column}
                        onClick={() =>
                          !isDisabled && onSelection
                            ? onSelection(row.original)
                            : {}
                        }
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            <EmptyState loading={isLoading} data={resolvedData.length} />
            {children}
          </TableWrapper>
        </div>
      </Container>
    </Container>
  );
};

// empty state
const EmptyState = ({ loading, data }: { loading: boolean; data: number }) => {
  if (loading || data === 0)
    return (
      <Container
        className={
          'w-full h-70 flex items-center justify-center p-5 text-md font-display text-blue-900'
        }
      >
        {loading ? (
          <Spinner size={'small'} />
        ) : (
          translate('This folder is empty')
        )}
      </Container>
    );
  return null;
};

interface CellProps<T extends object> {
  children: React.ReactNode;
  column: Column<T>;
  header?: boolean;
  onClick?: () => void;
}
// each cell
const TableCell = <T extends object>({
  children,
  column,
  header,
  onClick,
  ...rest
}: CellProps<T>) => {
  const cellCx = cx(
    'flex items-center antialiased py-5',
    column.id === 'name' && 'flex-grow flex-1 truncate',
    column.id === 'modified' ? 'min-w-40 hidden sm:flex' : 'min-w-12 ',
    'text-left',
    column.id === 'actions' && 'flex items-center justify-center min-w-16',
    !header && 'text-base',
    header &&
      'text-xs font-700 leading-8 text-cyan-700 tracking-wider uppercase select-none flex flex-row'
  );
  return (
    <div className={cellCx} onClick={onClick} {...rest}>
      {header ? (
        children
      ) : (
        <span
          className={cx('items-center truncate')}
          style={{
            display: column.id === 'name' ? 'block' : 'flex',
            color:
              column.id !== 'actions' && column.id !== 'type' ? 'inherit' : '',
          }}
        >
          {children}
        </span>
      )}
    </div>
  );
};

// row
interface RowProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  header?: boolean;
}
const TableRow = React.forwardRef<HTMLDivElement, RowProps>(function TableRow(
  { children, header, ...rest },
  ref
) {
  const rowCx = cx(
    'w-full flex flex-row px-2 text-blue-900',
    header && 'z-101 sticky top-0 bg-white',
    !header && 'hocus:text-cyan-700 transition-colors'
  );
  return (
    <div
      ref={ref}
      className={rowCx}
      style={{
        borderTop: header ? '1px solid rgb(232, 237, 240)' : '',
        borderBottom: '1px solid rgb(232, 237, 240)',
      }}
      {...rest}
    >
      {children}
    </div>
  );
});
